import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuildingOfficeIcon, MapPinIcon, PhoneIcon, UserIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useLocations, useMutation } from '../hooks/useApi';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTypeContext } from '../contexts/BusinessTypeContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { Location } from '../types/api';

interface FirstLocationFormData {
  name: string;
  address?: string;
  description?: string;
}

const LocationSelection: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { setSelectedLocation } = useLocation();
  const { setBusinessType } = useBusinessTypeContext();
  const { data: allLocations, loading, error, refetch } = useLocations();
  const [selectedBusinessType, setSelectedBusinessType] = useState<{ [key: string]: 'fish' | 'goat' }>({});
  const [showFirstFarmForm, setShowFirstFarmForm] = useState(false);
  const [firstFarmName, setFirstFarmName] = useState('');
  const [firstFarmAddress, setFirstFarmAddress] = useState('');
  const [firstFarmDescription, setFirstFarmDescription] = useState('');
  const [firstFarmBusinessTypes, setFirstFarmBusinessTypes] = useState<string[]>(['fish']);
  const [createError, setCreateError] = useState<string | null>(null);
  const { mutate: createLocation, loading: creatingLocation } = useMutation<Location, FirstLocationFormData & { business_types?: string[] }>();

  // Backend already returns only assigned locations for non-admin; use API response as source of truth
  // (avoids stale user.assigned_locations after creating first location)
  const locations = allLocations || [];

  const handleLocationSelect = async (location: Location) => {
    const businessTypes = location.business_types || ['fish'];
    const selectedType = businessTypes.length > 1
      ? (selectedBusinessType[location.id] || 'fish')
      : (businessTypes[0] as 'fish' | 'goat');
    const dashboardPath = selectedType === 'goat' ? '/goat/dashboard' : '/dashboard';

    try {
      await setSelectedLocation(location);
      setBusinessType(selectedType);
      // Pass location in state so DashboardLayout has it even if context hasn't flushed yet; prevents redirect loop
      navigate(dashboardPath, { state: { selectedLocation: location }, replace: true });
    } catch (error) {
      console.error('Error selecting location:', error);
      setBusinessType(selectedType);
      // Do not navigate on error – would cause redirect loop when PATCH fails (e.g. 403)
    }
  };

  const handleBusinessTypeSelect = (locationId: string, type: 'fish' | 'goat', e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent location selection
    setSelectedBusinessType(prev => ({ ...prev, [locationId]: type }));
  };

  const handleFirstFarmBusinessTypeChange = (type: 'fish' | 'goat', checked: boolean) => {
    setFirstFarmBusinessTypes((prev) => {
      if (checked) return [...prev.filter((t) => t !== type), type];
      const next = prev.filter((t) => t !== type);
      return next.length > 0 ? next : [type];
    });
  };

  const handleCreateFirstFarm = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (!firstFarmName.trim()) {
      setCreateError('Farm name is required');
      return;
    }
    const businessTypes = firstFarmBusinessTypes.length > 0 ? firstFarmBusinessTypes : ['fish'];
    try {
      const newLocation = await createLocation('/locations', {
        name: firstFarmName.trim(),
        address: firstFarmAddress.trim() || undefined,
        description: firstFarmDescription.trim() || undefined,
        business_types: businessTypes,
      });
      if (newLocation) {
        await refetch();
        await setSelectedLocation(newLocation as Location);
        const initialType = businessTypes.includes('goat') && !businessTypes.includes('fish')
          ? 'goat'
          : 'fish';
        setBusinessType(initialType);
        navigate(initialType === 'goat' ? '/goat/dashboard' : '/dashboard');
      }
    } catch (err: any) {
      setCreateError(err.response?.data?.detail || err.message || 'Failed to create farm');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading locations: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900">FarmLog.in</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
              <button
                onClick={logout}
                className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <BuildingOfficeIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Select Farm Location</h2>
          <p className="text-lg text-gray-600">
            Choose a farm location to manage your aquaculture operations
          </p>
        </div>

        {locations && locations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location) => (
              <div
                key={location.id}
                onClick={() => handleLocationSelect(location)}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all duration-200 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <BuildingOfficeIcon className="h-10 w-10 text-blue-600 group-hover:text-blue-700" />
                  <span className="text-2xl">🏢</span>
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-700">
                  {location.name}
                </h3>
                
                {location.address && (
                  <div className="flex items-start space-x-2 mb-2">
                    <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5" />
                    <p className="text-sm text-gray-600">{location.address}</p>
                  </div>
                )}
                
                {location.contact_person && (
                  <div className="flex items-center space-x-2 mb-2">
                    <UserIcon className="h-4 w-4 text-gray-400" />
                    <p className="text-sm text-gray-600">{location.contact_person}</p>
                  </div>
                )}
                
                {location.contact_phone && (
                  <div className="flex items-center space-x-2 mb-4">
                    <PhoneIcon className="h-4 w-4 text-gray-400" />
                    <p className="text-sm text-gray-600">{location.contact_phone}</p>
                  </div>
                )}
                
                {/* Business Type Badges */}
                <div className="mb-3 flex flex-wrap gap-2">
                  {(location.business_types || ['fish']).map((type: string) => (
                    <span
                      key={type}
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        type === 'fish'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {type === 'fish' ? '🐟 Fish' : '🐐 Goat'}
                    </span>
                  ))}
                </div>

                {/* Business Type Selector for locations with both types */}
                {(location.business_types || ['fish']).length > 1 && (
                  <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600 mb-2">Select business type:</p>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => handleBusinessTypeSelect(location.id, 'fish', e)}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium rounded ${
                          selectedBusinessType[location.id] === 'fish' || !selectedBusinessType[location.id]
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-blue-600 border border-blue-300'
                        }`}
                      >
                        🐟 Fish
                      </button>
                      <button
                        onClick={(e) => handleBusinessTypeSelect(location.id, 'goat', e)}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium rounded ${
                          selectedBusinessType[location.id] === 'goat'
                            ? 'bg-green-600 text-white'
                            : 'bg-white text-green-600 border border-green-300'
                        }`}
                      >
                        🐐 Goat
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    {(location.business_types || ['fish']).includes('fish') && (
                      <span>🏊‍♂️ {location.pond_count} Ponds</span>
                    )}
                    {(location.business_types || ['fish']).includes('goat') && (
                      <span>🏠 Goat Farming</span>
                    )}
                  </div>
                  <div className="text-blue-600 group-hover:text-blue-700">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
                
                {location.description && (
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                    {location.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {user?.role === 'admin' ? 'No Locations Available' : 'Create your first farm'}
            </h3>
            <p className="text-gray-600 mb-6">
              {user?.role === 'admin'
                ? 'Create your first farm location to get started.'
                : 'Add your farm to start managing locations, ponds, and data.'}
            </p>
            {user?.role === 'admin' ? (
              <button
                onClick={() => navigate('/admin/locations')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Create First Location
              </button>
            ) : showFirstFarmForm ? (
              <form onSubmit={handleCreateFirstFarm} className="max-w-md mx-auto text-left bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                {createError && (
                  <p className="mb-4 text-sm text-red-600">{createError}</p>
                )}
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-3">What do you farm?</p>
                  <p className="text-xs text-gray-500 mb-3">Choose one or both. You can switch between fish and goat dashboards anytime.</p>
                  <div className="flex flex-wrap gap-4">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={firstFarmBusinessTypes.includes('fish')}
                        onChange={(e) => handleFirstFarmBusinessTypeChange('fish', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Fish / aquaculture</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={firstFarmBusinessTypes.includes('goat')}
                        onChange={(e) => handleFirstFarmBusinessTypeChange('goat', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Goat farming</span>
                    </label>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Farm name *</label>
                  <input
                    type="text"
                    value={firstFarmName}
                    onChange={(e) => setFirstFarmName(e.target.value)}
                    placeholder="e.g. My Farm"
                    className="w-full form-input rounded-md border border-gray-300 px-3 py-2"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address (optional)</label>
                  <input
                    type="text"
                    value={firstFarmAddress}
                    onChange={(e) => setFirstFarmAddress(e.target.value)}
                    placeholder="Village, district, state"
                    className="w-full form-input rounded-md border border-gray-300 px-3 py-2"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <textarea
                    value={firstFarmDescription}
                    onChange={(e) => setFirstFarmDescription(e.target.value)}
                    placeholder="Notes about this farm"
                    rows={2}
                    className="w-full form-input rounded-md border border-gray-300 px-3 py-2"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={creatingLocation}
                    className="flex-1 flex justify-center items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                  >
                    {creatingLocation ? <LoadingSpinner size="sm" /> : null}
                    Create farm
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowFirstFarmForm(false); setCreateError(null); }}
                    className="px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => {
                  setShowFirstFarmForm(true);
                  setFirstFarmBusinessTypes(['fish']);
                  setFirstFarmName('');
                  setFirstFarmAddress('');
                  setFirstFarmDescription('');
                  setCreateError(null);
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                <PlusIcon className="h-5 w-5" />
                Create your first farm
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationSelection;